/**
 * RegionalCalendar — the region layer above the store board, inserted the same
 * way stage A inserted the floor board (2026-08-10): the screen renders the
 * Regional Calendar prototype ITSELF (a patched copy at
 * app/public/region-board/board.dc.html, chromeless), so layout, brand and
 * behavior are the prototype's own — deliberately NOT the app design system
 * (sanctioned exception, docs/design-baseline.md).
 *
 * Data flows one way in, navigation and drill flow back out:
 *   region-data  ← useRegionCalendar + toRegionData (branch roster, live signals)
 *   floor-data   ← useFloorCalendarDay for the branch the RM drilled into
 *   region-nav   → real router navigation (Event Day / Concierge)
 *   region-drill → which branch to fetch a day for
 *
 * Persona gating: the route's read capability is `region.read` (routeTable), the
 * regional manager's cap — a store manager can't deep-link in and sees the Floor
 * board instead, which is the ratified RM-both-boards / SM-theirs split. The
 * drilled board is read-only in the prototype for exactly the same reason.
 *
 * Writes: board-internal actions are prototype-local this stage, as on the floor
 * board. They swap to real ops during the verbatim TSX port.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { EmptyState } from "../../shell/composites";
import { useNav } from "../../shell/router";
import { useT } from "../../lib/i18n";
import { civilTodayInZone, isFloorDayFor, isValidIanaZone, toDCData, useFloorCalendarDay } from "./floor-calendar-data";
import { NO_REGION_SCOPE_MSG, toRegionData, useRegionCalendar } from "./regional-calendar-data";

/** The drilled branch's day. A child component so the day ops fire only once the
 *  RM actually opens a branch — the region view itself needs none of them. */
function DrilledStoreDay({ dateISO, tz, locationId, onDay, onState }: {
  dateISO: string; tz: string; locationId: string;
  onDay: (data: Record<string, unknown>) => void;
  onState: (state: { type: "floor-day-loading" | "floor-day-error"; storeId: string; message?: string }) => void;
}) {
  const { day, error } = useFloorCalendarDay(dateISO, locationId);
  /* The hook keeps the previous branch's (or date's) day while the new one
   * loads; only the response to THIS branch and date may be posted. */
  const fresh = isFloorDayFor(day, dateISO, locationId);
  /* The payload is STAMPED with the branch it describes: the board computes every
   * region card through the same generators, so an unstamped day would be read as
   * every branch's day. Its anchor is the BRANCH's civil Today in the branch zone. */
  useEffect(() => {
    if (fresh && day) onDay({ ...toDCData(day), storeId: locationId, anchor: { today: dateISO, tz } });
    else if (error && !day) onState({ type: "floor-day-error", storeId: locationId, message: error });
    else onState({ type: "floor-day-loading", storeId: locationId });
  }, [fresh, day, error, dateISO, tz, locationId, onDay, onState]);
  return null;
}

export default function RegionalCalendar() {
  const t = useT();
  const nav = useNav();
  const { region, loading, error, scopeMissing } = useRegionCalendar();
  const regionZoneError = region && !isValidIanaZone(region.tz) ? t("regionCal.timezoneInvalid", "The region did not return a valid majority IANA time zone. Regional Today is unavailable until branch time zones are configured.") : null;
  /* The region's civil Today in its majority zone: the anchor for every date the
   * region overview draws (labels, Today, day offsets). */
  const regionToday = region && !regionZoneError ? civilTodayInZone(new Date(), region.tz as string) : null;
  const [drillId, setDrillId] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [boardReady, setBoardReady] = useState(false);
  const drillZone = region?.stores.find((s) => s.id === drillId)?.tz || null;
  const drillZoneError = drillId && !isValidIanaZone(drillZone) ? t("regionCal.branchTimezoneInvalid", "The selected branch did not return a valid IANA time zone.") : null;
  const drillDateISO = isValidIanaZone(drillZone) ? civilTodayInZone(new Date(), drillZone) : null;
  const loadingTitle = t("regionCal.loadingTitle", "Loading the calendar");
  const regionLoadingMsg = t("regionCal.loading", "Loading the region…");
  const branchLoadingMsg = t("regionCal.branchLoading", "Loading this branch's day…");
  const zoneTitle = t("regionCal.timezoneMissing", "Calendar time zone unavailable");
  const failTitle = t("regionCal.pending", "The region board couldn't load");
  const branchFailTitle = t("regionCal.branchPending", "This branch's day couldn't load");

  /* Public assets resolve through __STUDIO_ASSET_BASE__ (app/public) — a
   * route-relative src would resolve against /floor/region and 404. */
  const boardSrc = useMemo(() => {
    const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
    return (base ? base.replace(/\/$/, "") + "/" : "/") + "region-board/board.dc.html?chromeless=1";
  }, []);

  const post = useCallback((msg: Record<string, unknown>) => {
    const w = frameRef.current?.contentWindow;
    if (w) w.postMessage(msg, window.location.origin);
  }, []);

  const pushRegion = useCallback(() => {
    if (region && regionToday) post({ type: "region-data", data: { ...toRegionData(region), anchor: { today: regionToday, tz: region.tz } } });
  }, [region, regionToday, post]);

  const pushDay = useCallback((data: Record<string, unknown>) => {
    post({ type: "floor-data", data });
  }, [post]);

  const pushDayState = useCallback((s: { type: "floor-day-loading" | "floor-day-error"; storeId: string; message?: string }) => {
    post(s.type === "floor-day-error"
      ? { type: s.type, storeId: s.storeId, title: branchFailTitle, message: s.message || "" }
      : { type: s.type, storeId: s.storeId, title: loadingTitle, message: branchLoadingMsg });
  }, [post, branchFailTitle, loadingTitle, branchLoadingMsg]);

  useEffect(() => {
    if (!boardReady) return;
    if (regionZoneError) post({ type: "region-error", title: zoneTitle, message: regionZoneError });
    else if (!region && error) post({ type: "region-error", title: failTitle, message: error });
    else if (!region) post({ type: "region-loading", title: loadingTitle, message: regionLoadingMsg });
    else pushRegion();
  }, [boardReady, pushRegion, post, region, error, regionZoneError, zoneTitle, failTitle, loadingTitle, regionLoadingMsg]);

  useEffect(() => {
    if (boardReady && drillId && drillZoneError) post({ type: "floor-timezone-error", storeId: drillId, title: zoneTitle, message: drillZoneError });
  }, [boardReady, drillId, drillZoneError, zoneTitle, post]);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      /* Same origin gate as FloorCalendar, same reason — see the note there. */
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as {
        type?: string; storeId?: string | null;
        detail?: { surface?: string; params?: Record<string, unknown> };
      } | null;
      if (!m || typeof m !== "object") return;
      if (m.type === "region-board-ready") { setBoardReady(true); return; }
      if (m.type === "region-drill") { setDrillId(m.storeId ? String(m.storeId) : null); return; }
      if (m.type === "region-nav" && m.detail) {
        const p = m.detail.params || {};
        if (m.detail.surface === "eventday" && p.eventId) { nav.go("eventday", { eventId: String(p.eventId) }); return; }
        if (m.detail.surface === "concierge") {
          /* the board speaks served queue ids here (its roster is served), so the
           * console param passes through untranslated */
          const q = String((p.queueId as string) || "");
          nav.go("queues.console", q ? { q } : {});
          return;
        }
        if (m.detail.surface === "eventsIndex") nav.go("events");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [nav]);

  if (scopeMissing) {
    return (
      <Screen rail="region-calendar">
        <EmptyState title={t("regionCal.noScope", "No region assigned")} body={NO_REGION_SCOPE_MSG} />
      </Screen>
    );
  }

  return (
    <Screen rail="region-calendar">
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <DataSourceStrip
          screen="floor.region"
          ops={["getQueueDashboard", "getAppointmentsReport", "listStaff", "listQueues"]}
          reqId="REQ-CAL-1"
        />
        <div style={{ padding: "6px 14px 0", fontSize: 12, color: T.soft }}>
          {t("regionCal.derivedNote", "Branch names, managers, waits, no-shows, volumes, time zones and the SLA are served; the health score is derived from them. Revenue, utilisation, appointment and event-fill figures and the alert feed are still the prototype's own until REQ-CAL-1 and REQ-CAL-6 land.")}
        </div>
        {drillId && drillZone && drillDateISO && !drillZoneError ? <DrilledStoreDay dateISO={drillDateISO} tz={drillZone} locationId={drillId} onDay={pushDay} onState={pushDayState} /> : null}
        {(error || regionZoneError || drillZoneError) && !region ? <EmptyState title={t("regionCal.pending", "The region board couldn't load")} body={error || regionZoneError || drillZoneError || ""} /> : null}
        {regionZoneError || drillZoneError ? <EmptyState title={t("regionCal.timezoneMissing", "Calendar time zone unavailable")} body={regionZoneError || drillZoneError || ""} /> : null}
        {loading && !region ? (
          <div style={{ padding: 18, fontSize: 13.5, color: T.soft }}>{t("regionCal.loading", "Loading the region…")}</div>
        ) : null}
        <iframe
          ref={frameRef} title={t("floor.regionalCalendar.title", "Regional Calendar")} src={boardSrc}
          onLoad={() => { pushRegion(); }}
          style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "#F4F5F7" }}
        />
      </div>
    </Screen>
  );
}
